<template>
  <div class="page-container">
    <el-card class="page-content" shadow="never">
      <div class="page-title">体验金数据统计</div>

      <div class="page-actions">
        <div class="select">
          <div class="item">
            <el-input
              v-model.trim="filters.userUid"
              placeholder="用户UID"
              clearable
              size="mini"
              @keyup.enter.native="handleSearch"
            />
          </div>
          <div class="item">
            <el-input
              v-model.trim="filters.partnerUid"
              placeholder="合伙人UID"
              clearable
              size="mini"
              @keyup.enter.native="handleSearch"
            />
          </div>
          <div class="item">
            <el-input
              v-model.trim="filters.couponNo"
              placeholder="体验金卡券ID"
              clearable
              size="mini"
              @keyup.enter.native="handleSearch"
            />
          </div>
          <div class="item">
            <el-date-picker
              v-model="filters.dateRange"
              type="daterange"
              value-format="yyyy-MM-dd"
              format="yyyy-MM-dd"
              range-separator="-"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              size="mini"
              unlink-panels
            />
          </div>
          <div class="item">
            <el-button size="mini" type="primary" @click="handleSearch">查询</el-button>
          </div>
          <div class="item">
            <el-button size="mini" @click="handleReset">重置</el-button>
          </div>
          <div class="item">
            <el-button size="mini" type="primary" :loading="exportLoading" @click="handleExport">
              导出
            </el-button>
          </div>
        </div>
      </div>

      <div class="summary" v-loading="summaryLoading">
        <div class="summary-item">
          <span>总体验金抵扣金额：</span>
          <strong>{{ formatAmount(summary.totalDeductAmount) }} USDT</strong>
        </div>
        <div class="summary-item">
          <span>总体验金抵扣手续费金额：</span>
          <strong>{{ formatAmount(summary.totalFeeDeductAmount) }} USDT</strong>
        </div>
        <div class="summary-item">
          <span>总体验金抵扣资金费金额：</span>
          <strong>{{ formatAmount(summary.totalFundingDeductAmount) }} USDT</strong>
        </div>
        <div class="summary-item">
          <span>总体验金抵扣盈亏金额：</span>
          <strong>{{ formatAmount(summary.totalPnlDeductAmount) }} USDT</strong>
        </div>
      </div>

      <el-table
        :data="tableList"
        height="calc(100vh - 390px)"
        size="mini"
        v-loading="loading"
      >
        <el-table-column prop="userUid" label="用户UID" min-width="110" />
        <el-table-column prop="userTypeDesc" label="用户类型" min-width="100">
          <template slot-scope="scope">{{ formatValue(scope.row.userTypeDesc) }}</template>
        </el-table-column>
        <el-table-column prop="partnerUid" label="合伙人UID" min-width="110">
          <template slot-scope="scope">{{ formatValue(scope.row.partnerUid) }}</template>
        </el-table-column>
        <el-table-column prop="statisticsDate" label="统计日期" min-width="110" />
        <el-table-column prop="couponIds" label="体验金卡券ID" min-width="190">
          <template slot-scope="scope">
            <coupon-id-list
              :items="scope.row.couponIds"
              :summary-items="couponSummaryItems(scope.row)"
              dialog-title="体验金卡券ID"
              item-label="体验金卡券ID"
              empty-text="--"
              show-count
            />
          </template>
        </el-table-column>
        <el-table-column prop="grantAmount" label="体验金领取金额" min-width="130">
          <template slot-scope="scope">{{ formatAmount(scope.row.grantAmount) }}</template>
        </el-table-column>
        <el-table-column prop="recycleAmount" label="体验金回收金额" min-width="130">
          <template slot-scope="scope">{{ formatAmount(scope.row.recycleAmount) }}</template>
        </el-table-column>
        <el-table-column prop="deductAmount" label="体验金抵扣金额" min-width="130">
          <template slot-scope="scope">{{ formatAmount(scope.row.deductAmount) }}</template>
        </el-table-column>
        <el-table-column prop="feeDeductAmount" label="体验金抵扣手续费金额" min-width="160">
          <template slot-scope="scope">
            <el-button type="text" size="mini" @click="openDetail(scope.row, 'FEE')">
              {{ formatAmount(scope.row.feeDeductAmount) }}
            </el-button>
          </template>
        </el-table-column>
        <el-table-column prop="fundingDeductAmount" label="体验金抵扣资金费金额" min-width="160">
          <template slot-scope="scope">{{ formatAmount(scope.row.fundingDeductAmount) }}</template>
        </el-table-column>
        <el-table-column prop="pnlDeductAmount" label="体验金抵扣盈亏金额" min-width="160">
          <template slot-scope="scope">{{ formatAmount(scope.row.pnlDeductAmount) }}</template>
        </el-table-column>
        <el-table-column
          prop="fundingFeePreDeductAmount"
          label="待结算资金费抵扣金额"
          min-width="180"
        >
          <template slot-scope="scope">
            <el-button type="text" size="mini" @click="openDetail(scope.row, 'FUNDING_FEE')">
              {{ formatAmount(scope.row.fundingFeePreDeductAmount) }}
            </el-button>
          </template>
        </el-table-column>
        <el-table-column
          prop="cyclePnlConvertedAmount"
          label="待结算盈亏抵扣金额"
          min-width="180"
        >
          <template slot-scope="scope">
            <el-button type="text" size="mini" @click="openDetail(scope.row, 'PNL')">
              {{ formatAmount(scope.row.cyclePnlConvertedAmount) }}
            </el-button>
          </template>
        </el-table-column>
        <el-table-column prop="deductRate" label="抵扣比例" min-width="100">
          <template slot-scope="scope">{{ formatRate(scope.row.deductRate) }}</template>
        </el-table-column>
        <el-table-column prop="remainingAmount" label="体验金余额" min-width="120">
          <template slot-scope="scope">{{ formatAmount(scope.row.remainingAmount) }}</template>
        </el-table-column>
        <el-table-column label="回收状态" min-width="160" fixed="right">
          <template slot="header">
            <el-tooltip content="展示该体验金周期当前状态；金额仍为对应统计日期的数据" placement="top">
              <span>回收状态 <i class="el-icon-info" /></span>
            </el-tooltip>
          </template>
          <template slot-scope="scope"><manual-operation :row="scope.row" display="status" /></template>
        </el-table-column>
        <el-table-column label="操作" width="100" fixed="right">
          <template slot-scope="scope"><manual-operation :row="scope.row" @refresh="fetchData" /></template>
        </el-table-column>
      </el-table>

      <div class="pag">
        <pagination
          :size="pagination.size"
          :total="totalCount"
          :page="pagination.page"
          @pageChanged="pageChanged"
        />
      </div>

      <el-dialog
        :title="detailDialogTitle"
        :visible.sync="detailVisible"
        width="880px"
        :destroy-on-close="true"
        append-to-body
        custom-class="fee-detail-dialog"
      >
        <div v-if="detailType === 'FEE'" v-loading="detailLoading" class="fee-detail-content">
          <div class="fee-detail-summary">
            <div class="fee-detail-summary-item">
              <span>用户UID：</span>
              <strong>{{ formatValue(detailRow.userUid) }}</strong>
            </div>
            <div class="fee-detail-summary-item">
              <span>合伙人UID：</span>
              <strong>{{ formatValue(detailRow.partnerUid) }}</strong>
            </div>
            <div class="fee-detail-summary-item">
              <span>统计日期：</span>
              <strong>{{ formatValue(detailRow.statisticsDate) }}</strong>
            </div>
            <div class="fee-detail-summary-item fee-detail-total">
              <span>体验金抵扣手续费金额：</span>
              <strong>{{ formatPopupAmount(detailRow.feeDeductAmount) }}</strong>
            </div>
            <div class="fee-detail-summary-item">
              <span>抵扣比例：</span>
              <strong>{{ formatRate(detailRow.deductRate) }}</strong>
            </div>
          </div>

          <div class="fee-detail-table-header">
            <span>抵扣时间</span>
            <span>业务类型</span>
            <span>仓位ID</span>
            <span>原始手续费</span>
            <span>体验金抵扣手续费金额</span>
          </div>
          <div class="fee-detail-table-body">
            <div
              v-for="(item, index) in detailList"
              :key="item.billId || `${item.positionId}-${index}`"
              class="fee-detail-table-row"
            >
              <span>{{ formatTime(item.createdTime) }}</span>
              <span>{{ detailBusinessType(item) }}</span>
              <span>{{ formatValue(item.positionId) }}</span>
              <span>{{ formatPopupAmount(item.originalAmount) }}</span>
              <span class="fee-detail-amount">{{ formatPopupAmount(item.amount) }}</span>
            </div>
            <div v-if="!detailLoading && !detailList.length" class="fee-detail-empty">
              暂无数据
            </div>
          </div>
        </div>

        <div v-else v-loading="detailLoading" class="fee-detail-content">
          <div class="fee-detail-summary">
            <div class="fee-detail-summary-item">
              <span>用户UID：</span>
              <strong>{{ formatValue(detailRow.userUid) }}</strong>
            </div>
            <div class="fee-detail-summary-item">
              <span>合伙人UID：</span>
              <strong>{{ formatValue(detailRow.partnerUid) }}</strong>
            </div>
            <div class="fee-detail-summary-item">
              <span>统计日期：</span>
              <strong>{{ formatValue(detailRow.statisticsDate) }}</strong>
            </div>
            <div class="fee-detail-summary-item">
              <span>{{ pendingDailyLabel }}：</span>
              <strong :class="signedAmountClass(pendingDailyValue)">
                {{ formatPendingSummary(pendingDailyValue) }}
              </strong>
            </div>
            <div class="fee-detail-summary-item">
              <span>抵扣比例：</span>
              <strong>{{ pendingRateText() }}</strong>
            </div>
          </div>

          <div class="fee-detail-table-header">
            <span>发生时间</span>
            <span>业务类型</span>
            <span>仓位ID</span>
            <span>{{ pendingOriginalAmountLabel }}</span>
            <span>{{ pendingAmountLabel }}</span>
          </div>
          <div class="fee-detail-table-body">
            <div
              v-for="(item, index) in pendingDetail.items"
              :key="item.billId || `${item.positionId}-${index}`"
              class="fee-detail-table-row"
            >
              <span>{{ formatTime(item.createdTime) }}</span>
              <span>{{ detailBusinessType(item) }}</span>
              <span>{{ formatValue(item.positionId) }}</span>
              <span :class="signedAmountClass(item.originalAmount)">
                {{ formatSignedPopupAmount(item.originalAmount) }}
              </span>
              <span :class="signedAmountClass(item.pendingDeductAmount)">
                {{ formatSignedPopupAmount(item.pendingDeductAmount) }}
              </span>
            </div>
            <div
              v-if="!detailLoading && !pendingDetail.items.length"
              class="fee-detail-empty"
            >
              暂无数据
            </div>
          </div>
        </div>

        <div slot="footer" class="fee-detail-footer">
          <el-button type="primary" @click="detailVisible = false">
            关闭
          </el-button>
        </div>
      </el-dialog>
    </el-card>
  </div>
</template>

<script>
import moment from 'moment'
import Decimal from 'decimal.js'
import Pagination from 'components/pagination'
import CouponIdList from '../components/CouponIdList'
import api from './api'
import ManualOperation from '../components/ManualOperation'

const PAGINATION = {
  page: 1,
  size: 10,
}

function createFilters() {
  return {
    userUid: '',
    partnerUid: '',
    couponNo: '',
    dateRange: [
      moment().subtract(7, 'days').format('YYYY-MM-DD'),
      moment().format('YYYY-MM-DD'),
    ],
  }
}

function createSummary() {
  return {
    totalDeductAmount: '',
    totalFeeDeductAmount: '',
    totalFundingDeductAmount: '',
    totalPnlDeductAmount: '',
  }
}

function createPendingDetail() {
  return {
    fundingFeePendingDeductAmount: null,
    pnlPendingDeductAmount: null,
    pendingDeductAmount: null,
    unknownAmountCount: 0,
    items: [],
  }
}

export default {
  name: 'WelfareBonusStatistic',
  components: { Pagination, CouponIdList, ManualOperation },
  data() {
    return {
      filters: createFilters(),
      pagination: { ...PAGINATION },
      tableList: [],
      totalCount: 0,
      summary: createSummary(),
      loading: false,
      summaryLoading: false,
      exportLoading: false,
      detailVisible: false,
      detailLoading: false,
      detailType: '',
      detailList: [],
      detailRow: {},
      pendingDetail: createPendingDetail(),
    }
  },
  computed: {
    detailDialogTitle() {
      const titleMap = {
        FEE: '手续费抵扣明细',
        FUNDING_FEE: '待结算资金费抵扣明细',
        PNL: '待结算盈亏抵扣明细',
      }
      return titleMap[this.detailType] || '待结算抵扣明细'
    },
    pendingDailyLabel() {
      return this.detailType === 'FUNDING_FEE'
        ? '待结算抵扣金额'
        : '待结算盈亏金额'
    },
    pendingDailyValue() {
      return this.detailType === 'FUNDING_FEE'
        ? this.pendingDetail.fundingFeePendingDeductAmount
        : this.pendingDetail.pnlPendingDeductAmount
    },
    pendingOriginalAmountLabel() {
      return this.detailType === 'FUNDING_FEE' ? '原始资金费' : '原始盈亏'
    },
    pendingAmountLabel() {
      return this.detailType === 'FUNDING_FEE' ? '待结算抵扣金额' : '待结算抵扣金额'
    },
  },
  created() {
    this.fetchData()
  },
  methods: {
    formatValue(value) {
      return value === null || value === undefined || value === '' ? '--' : value
    },
    formatAmount(value) {
      if (value === null || value === undefined || value === '') return '--'
      try {
        return new Decimal(value).toDecimalPlaces(4, Decimal.ROUND_DOWN).toFixed(4)
      } catch (error) {
        return value
      }
    },
    formatPopupAmount(value, signed = false) {
      if (value === null || value === undefined || value === '') return '--'
      try {
        const number = new Decimal(value)
        const displayNumber = signed ? number : number.abs()
        const amount = displayNumber.toDecimalPlaces(4, Decimal.ROUND_DOWN).toFixed(4)
        const sign = signed && displayNumber.gt(0) ? '+' : ''
        return `${sign}${amount}${this.detailRow.coin ? ` ${this.detailRow.coin}` : ''}`
      } catch (error) {
        return value
      }
    },
    formatSignedPopupAmount(value) {
      return this.formatPopupAmount(value, true)
    },
    formatPendingSummary(value) {
      if (value === null || value === undefined || value === '') {
        return Number(this.pendingDetail.unknownAmountCount) > 0
          ? '-- / 历史数据不完整'
          : '--'
      }
      return this.formatSignedPopupAmount(value)
    },
    pendingRateText() {
      const rates = this.pendingDetail.items
        .map((item) => item.deductRateSnapshot)
        .filter((value) => value !== null && value !== undefined && value !== '')
        .map(String)
      const uniqueRates = [...new Set(rates)]
      if (!uniqueRates.length) {
        return Number(this.pendingDetail.unknownAmountCount) > 0
          ? '-- / 历史数据不完整'
          : '--'
      }
      return uniqueRates.length === 1 ? `${uniqueRates[0]}%` : '以明细为准'
    },
    detailBusinessType(item) {
      const typeMap = {
        404: '手续费',
        407: '资金费',
        408: '资金费',
        409: '盈亏',
        410: '盈亏',
        411: '盈亏',
      }
      return typeMap[Number(item.type)] || '--'
    },
    signedAmountClass(value) {
      const number = Number(value)
      if (!Number.isFinite(number) || number === 0) return ''
      return number > 0 ? 'amount-positive' : 'amount-negative'
    },
    formatRate(value) {
      return value === null || value === undefined || value === '' ? '--' : `${value}%`
    },
    formatTime(value) {
      if (!value) return '--'

      const time = Array.isArray(value)
        ? moment.utc([
          value[0],
          value[1] - 1,
          value[2],
          value[3] || 0,
          value[4] || 0,
          value[5] || 0,
          value[6] || 0,
        ]).local()
        : moment(value)

      return time.isValid()
        ? time.format('YYYY-MM-DD HH:mm:ss')
        : value
    },
    couponSummaryItems(row) {
      return [
        { label: '用户UID', value: row.userUid },
        { label: '合伙人UID', value: row.partnerUid },
        { label: '统计日期', value: row.statisticsDate },
      ]
    },
    buildQuery(includePagination = true) {
      const { userUid, partnerUid, couponNo, dateRange } = this.filters
      const query = {
        startDate: dateRange && dateRange[0],
        endDate: dateRange && dateRange[1],
      }
      if (userUid) query.userUid = userUid
      if (partnerUid) query.partnerUid = partnerUid
      if (couponNo) query.couponNo = couponNo
      if (includePagination) {
        query.page = this.pagination.page
        query.size = this.pagination.size
      }
      return query
    },
    validateDateRange() {
      if (!this.filters.dateRange || this.filters.dateRange.length !== 2) {
        this.$message.warning('请选择统计开始日期和结束日期')
        return false
      }
      return true
    },
    fetchData() {
      if (!this.validateDateRange()) return
      this.fetchList()
      this.fetchSummary()
    },
    fetchList() {
      this.loading = true
      return api
        .list(this.buildQuery())
        .then((res) => {
          if (res && res.code === 0) {
            const data = res.data || {}
            this.tableList = Array.isArray(data)
              ? data
              : data.items || data.records || data.list || []
            this.totalCount = Array.isArray(data)
              ? data.length
              : data.total != null
                ? data.total
                : data.totalCount != null
                  ? data.totalCount
                  : 0
          } else {
            this.tableList = []
            this.totalCount = 0
            this.$message.error((res && (res.msg || res.message)) || '查询失败')
          }
        })
        .catch(() => {
          this.tableList = []
          this.totalCount = 0
        })
        .finally(() => {
          this.loading = false
        })
    },
    fetchSummary() {
      this.summaryLoading = true
      return api
        .summary(this.buildQuery(false))
        .then((res) => {
          if (res && res.code === 0) {
            this.summary = {
              ...createSummary(),
              ...(res.data || {}),
            }
          } else {
            this.summary = createSummary()
            this.$message.error((res && (res.msg || res.message)) || '汇总查询失败')
          }
        })
        .catch(() => {
          this.summary = createSummary()
        })
        .finally(() => {
          this.summaryLoading = false
        })
    },
    handleSearch() {
      if (!this.validateDateRange()) return
      this.pagination.page = 1
      this.fetchData()
    },
    handleReset() {
      this.filters = createFilters()
      this.pagination = { ...PAGINATION }
      this.fetchData()
    },
    pageChanged({ page, size }) {
      this.pagination = { page, size }
      if (this.validateDateRange()) this.fetchList()
    },
    openDetail(row, detailType) {
      if (!row.userUid || !row.cycleId || !row.statisticsDate) {
        this.$message.warning('缺少明细查询所需参数')
        return
      }
      this.detailRow = row
      this.detailType = detailType
      this.detailVisible = true
      this.detailLoading = true
      this.detailList = []
      this.pendingDetail = createPendingDetail()

      const params = {
        userUid: row.userUid,
        cycleId: row.cycleId,
        statisticsDate: row.statisticsDate,
      }
      const request = detailType === 'FEE'
        ? api.detail({ ...params, detailType })
        : api.pendingDetail({ ...params, detailType })

      request
        .then((res) => {
          if (res && res.code === 0) {
            if (detailType !== 'FEE') {
              const data = res.data || {}
              this.pendingDetail = {
                ...createPendingDetail(),
                ...data,
                items: Array.isArray(data.items) ? data.items : [],
              }
            } else {
              const data = res.data || []
              this.detailList = Array.isArray(data)
                ? data
                : data.items || data.records || data.list || []
            }
          } else {
            this.$message.error((res && (res.msg || res.message)) || '明细查询失败')
          }
        })
        .catch(() => {
          this.detailList = []
          this.pendingDetail = createPendingDetail()
        })
        .finally(() => {
          this.detailLoading = false
        })
    },
    handleExport() {
      if (!this.validateDateRange() || this.exportLoading) return
      this.exportLoading = true
      api
        .exportExcel(this.buildQuery(false))
        .then((res) => {
          const blob = res instanceof Blob ? res : new Blob([res], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          })
          const url = URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.style.display = 'none'
          link.href = url
          link.download = `体验金数据统计${moment().format('YYYYMMDD')}001.xlsx`
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          URL.revokeObjectURL(url)
        })
        .catch(() => {
          this.$message.error('导出失败，请稍后重试')
        })
        .finally(() => {
          this.exportLoading = false
        })
    },
  },
}
</script>

<style lang="less" scoped>
.page-title {
  margin-bottom: 18px;
  color: #409eff;
  font-size: 16px;
}

.page-actions {
  margin-bottom: 12px;
}

.select {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.item {
  margin-right: 12px;
  margin-bottom: 8px;
}

.summary {
  display: grid;
  grid-template-columns: repeat(2, minmax(280px, 1fr));
  gap: 12px 36px;
  min-height: 54px;
  margin-bottom: 16px;
  font-size: 13px;
}

.summary-item strong {
  color: #e6a23c;
  font-weight: normal;
}

.pag {
  display: flex;
  justify-content: center;
}

.fee-detail-summary {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  row-gap: 12px;
  padding: 16px 20px;
  margin-bottom: 22px;
  color: #7d8799;
  background: #f6f8fb;
  border-radius: 6px;
}

.fee-detail-summary-item {
  display: flex;
  align-items: center;
  min-width: 0;
  line-height: 20px;
}

.fee-detail-summary-item strong {
  color: #647084;
  font-weight: normal;
}

.fee-detail-total strong {
  color: #ff8a4c;
}

.fee-detail-summary-item strong.amount-positive,
.amount-positive {
  color: #39bd83;
}

.fee-detail-summary-item strong.amount-negative,
.amount-negative {
  color: #ff7676;
}

.fee-detail-table-header,
.fee-detail-table-row {
  display: grid;
  grid-template-columns: 170px 105px minmax(170px, 1fr) 125px 150px;
  align-items: center;
  min-height: 48px;
  padding: 0 12px;
  color: #7d8799;
}

.fee-detail-table-header {
  border-bottom: 1px solid #e8edf3;
}

.fee-detail-table-body {
  max-height: 336px;
  padding-top: 12px;
  overflow-y: auto;
}

.fee-detail-table-row {
  border-radius: 6px;
}

.fee-detail-table-row:nth-child(odd) {
  background: #f6f8fb;
}

.fee-detail-table-row span {
  min-width: 0;
  word-break: break-all;
}

.fee-detail-amount {
  color: #4f8cff;
}

.fee-detail-empty {
  padding: 42px 0;
  color: #909399;
  text-align: center;
}

.fee-detail-footer {
  text-align: center;
}

::v-deep .fee-detail-dialog .el-dialog__header {
  padding: 28px 20px 14px;
  text-align: center;
}

::v-deep .fee-detail-dialog .el-dialog__title {
  color: #303849;
  font-size: 18px;
}

::v-deep .fee-detail-dialog .el-dialog__body {
  padding: 14px 42px 0;
}

::v-deep .fee-detail-dialog .el-dialog__footer {
  padding: 12px 20px 24px;
}

::v-deep .fee-detail-dialog .fee-detail-footer .el-button {
  min-width: 108px;
}
</style>
